import React, { useEffect, useState, Fragment } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import randomString from 'random-string';
import { useQuery, useMutation } from '@apollo/client';

import { GET_SESSION, UPDATE_MY_NOTE, GET_PRESENT_SESSION, checkIn } from '../../services/patient';

import { joinRoom, mindgymReport } from '../../services';

import { userRole } from '../../constant';

import { processMindgymReport } from '../../utils/mindgym';

import { alertError, alertSuccess } from '../../redux/stateActions';

import {
    AppointmentCard,
    SessionSummaryCard,
    PatientDetailCard,
    ConfirmDialog,
    QueCard,
    ProviderInfoCard,
    NoteCard,
    ContainedButton,
    Text,
    Title,
    Header,
    Progress,
    PDPADialog,
} from '../../components/index';

import { patientPageWidth } from '../../constant';

import socket from '../../socket';

const STATUS = Object.freeze({
    CHECK_IN: 'CHECK_IN',
    QUEUE: 'QUEUE',
    ON_CALLING: 'ON_CALLING',
    NOTE: 'NOTE',
    SUMMARY: 'SUMMARY',
    SUCCESS: 'SUCCESS',
});

const OneOnOneSessionDetail = ({ history, location }) => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const mobileS = useMediaQuery('(max-width:374.95px)');

    const { id } = useParams();
    const user = useSelector((state) => state.user);

    const [appointmentData, setAppointmentData] = useState(null);
    const [appointmentStatus, setAppointmentStatus] = useState(STATUS.CHECK_IN);
    const [queueData, setQueueData] = useState({});
    const [note, setNote] = useState('');

    const [header, setHeader] = useState({ title: 'Appointment' });
    const [open, setOpen] = useState(false);
    const [presentSession, setPresentsession] = useState(null);
    const [loading, setLoading] = useState(false);

    /*---------- Get session for Appointment ----------*/
    const {
        data: sessionData,
        error: sessionError,
        loading: sessionLoading,
        refetch: sessionRefetch,
    } = useQuery(GET_SESSION, {
        variables: { id: id },
        skip: !id,
        pollInterval: 30000,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    const handleGoHome = () => {
        history.push('/home');
    };

    useEffect(() => {
        if (sessionError) {
            dispatch(alertError(`Cannot get Session: ${sessionError.message}`));
            handleGoHome();
        }

        if (sessionData && _.isEmpty(sessionData.session)) {
            handleGoHome();
        }

        if (sessionData && !_.isEmpty(sessionData.session)) {
            const { search } = location;
            const queryParams = new URLSearchParams(search);
            const status = queryParams.get('status');

            setAppointmentData(sessionData.session);

            if (sessionData.session.patient_summaries[0].patient.id !== user.patient.id) {
                dispatch(alertError(`Unauthorized Session`));
                handleGoHome();
                return;
            }

            // check if session cancelled or ended
            if (sessionData.session.cancelled) {
                dispatch(alertError(`Session Cancelled`));
                handleGoHome();
                return;
            }
            if (sessionData.session.sessionEnd && sessionData.session.patient_summaries[0].myNote) {
                dispatch(alertSuccess(`Session Ended`));
                handleGoHome();
                return;
            }

            // check if my note already set
            if (sessionData.session.patient_summaries[0].myNote) {
                setAppointmentStatus(STATUS.SUCCESS);
            }

            // check if videos call has end
            else if (sessionData.session.actualEndTime || status === 'NOTE') {
                setAppointmentStatus(STATUS.NOTE);

                if (
                    user?.uuid &&
                    sessionData.session?.meet?.type === 'one' &&
                    _.isEmpty(mindgymActivity)
                ) {
                    handleGetMindGymReport(
                        sessionData.session?.meet?.staff?.id,
                        sessionData.session.id
                    );
                }
            }

            // check if videos call has start
            else if (sessionData.session.actualStartTime) {
                setAppointmentStatus(STATUS.ON_CALLING);
            }

            //checkif patient already check-in if check-in then go to queue
            else if (sessionData.session.patient_summaries[0].checkinTime) {
                const filteredSession = JSON.parse(
                    JSON.stringify(sessionData.session.meet.sessions)
                ).filter((session) => session.patient_summaries[0].checkinTime !== null);

                const sortedSessions = filteredSession.sort((a, b) =>
                    dayjs(a.patient_summaries[0].checkinTime).isAfter(
                        dayjs(b.patient_summaries[0].checkinTime)
                    )
                        ? 1
                        : -1
                );

                const queue = sortedSessions.findIndex((session) => {
                    return session.id === id;
                });

                const watingQueue = sortedSessions
                    .filter((session) => session.sessionEnd === false)
                    .findIndex((session) => {
                        return session.id === id;
                    });

                setQueueData({
                    queueNumber:
                        'FR' +
                        (queue + 1).toLocaleString('en-US', {
                            minimumIntegerDigits: 2,
                        }),
                    watingQueue: watingQueue + 1,
                    watingTime: (watingQueue + 1) * 30,
                });

                setAppointmentStatus(STATUS.QUEUE);
            }
        }
    }, [sessionError, sessionData, location]);

    /*--------------- Update My Note ---------------*/
    const [updateMyNote, { data: updateMyNoteData, error: updateMyNoteError }] =
        useMutation(UPDATE_MY_NOTE);

    useEffect(() => {
        if (updateMyNoteError) {
            dispatch(alertError(`Cannot update My Note: ${updateMyNoteError.message}`));
        }

        if (updateMyNoteData) {
            setAppointmentStatus(STATUS.SUCCESS);
        }
    }, [updateMyNoteError, updateMyNoteData]);

    /*--------------- Get Session ---------------*/
    const { data: getPresentData, error: getPresentError } = useQuery(GET_PRESENT_SESSION, {
        variables: {
            id: user.patient.id,
        },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getPresentError) {
            dispatch(alertError(`Cannot get Present Session Summary: ${getPresentError.message}`));
        }
        if (getPresentData && !_.isEmpty(getPresentData.sessions)) {
            setPresentsession(getPresentData.sessions[0]);
        }
    }, [getPresentError, getPresentData]);

    const handleGoback = () => {
        history.goBack();
    };

    useEffect(() => {
        switch (appointmentStatus) {
            case STATUS.QUEUE:
                return setHeader({ title: 'Waiting room' });
            case STATUS.NOTE:
                return setHeader({ title: 'Summary' });
            case STATUS.SUMMARY:
                return setHeader({
                    title: 'Summary',
                });
            case STATUS.SUCCESS:
                return setHeader({
                    title: 'Summary',
                    showNotice: true,
                });
        }
    }, [appointmentStatus]);

    /*--------------- PDPA ---------------*/
    const [openPDPA, setOpenPDPA] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const onCheckChange = (e) => {
        setAccepted(e.target.checked);
    };

    const handleClosePDPA = () => {
        setOpenPDPA(false);
        setAccepted(false);
    };

    const onAccept = () => {
        setOpenPDPA(false);
        handleClick();
    };

    const handleClick = async () => {
        if (!accepted) {
            setOpenPDPA(true);
            return;
        }

        if (appointmentStatus === STATUS.CHECK_IN) {
            setOpen(true);
        } else if (appointmentStatus === STATUS.ON_CALLING) {
            try {
                setLoading(true);

                const params = new URLSearchParams();
                params.append('roomId', appointmentData.roomId);
                params.append('sessionId', randomString({ length: 16 }).toLowerCase());
                params.append('role', userRole.PATIENT);
                params.append(
                    'displayName',
                    `${user.fullname}|${user.patient?.id}|${user.avatar?.url}|Patient`
                );
                params.append('password', '');

                const joinRes = await joinRoom(appointmentData.roomId, params);

                if (joinRes.data) {
                    // history.push({
                    //     pathname: `/call/${appointmentData.id}`,
                    //     search: `?roomId=${appointmentData.roomId}&session=${joinRes.data.session}`,
                    // });

                    window.location.href = `${window.location.origin}/call/${appointmentData.id}?roomId=${appointmentData.roomId}&session=${joinRes.data.session}`;
                } else {
                    throw new Error(joinRes);
                }
            } catch (error) {
                setLoading(false);
                console.log('one ', error);
                dispatch(alertError(`Cannot join open session: ${error.message}`));
            }
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        setLoading(true);
        handleClose();

        try {
            const res = await checkIn({
                patientSummaryId: appointmentData.patient_summaries[0].id,
                sessionId: appointmentData.id,
                meetId: appointmentData.meet.id,
                patientId: user.patient.id,
            });

            if (res.status === 200) {
                setLoading(false);
                sessionRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot check-in: ${error.message}`));
            setLoading(false);
        }
    };

    const handleNote = (value) => {
        setNote(value);
    };

    const handleSubmit = () => {
        if (appointmentStatus === STATUS.NOTE) {
            updateMyNote({
                variables: {
                    id: appointmentData.patient_summaries[0].id,
                    myNote: note,
                },
            });
        }
        if (appointmentStatus === STATUS.SUMMARY) {
            setAppointmentStatus(STATUS.SUCCESS);
        }
    };

    useEffect(() => {
        socket.on('providerCancelCall', () => {
            setAppointmentStatus(STATUS.QUEUE);
        });

        socket.on('providerSkipQueue', () => {
            setAppointmentStatus(STATUS.CHECK_IN);
            sessionRefetch();
        });

        socket.on('sessionEnd', (data) => {
            if (data?.sessionId === id) {
                sessionRefetch();
            }
        });
        socket.on('sessionUpdated', (data) => {
            if (data?.sessionId === id) {
                sessionRefetch();
            }
        });
    }, []);

    /*--------------- Mindgym Activity ---------------*/
    const [mindgymActivity, setMindgymActivity] = useState([]);

    const handleGetMindGymReport = async (staffId, sessionId) => {
        try {
            if (!staffId || !sessionId) return;

            const res = await mindgymReport(staffId, sessionId);

            if (res?.data?.statusCode === 200) {
                setMindgymActivity(processMindgymReport(res?.data?.data));
            }
        } catch (error) {
            console.log('error', error);
            dispatch(
                alertError(`Cannot get Friday Activity: ${error.response?.data?.message}`)
            );
        }
    };

    return (
        <div
            style={{
                padding: mobileS ? '0 5px 100px' : '0 15px 100px',
                width: '100%',
                maxWidth: patientPageWidth,
            }}
        >
            <Header
                showGoBack1
                onClickGoBack={handleGoback}
                showNotice={header.showNotice}
                childrenContainerStyle={{ textAlign: 'center' }}
            >
                <Text $bold>{header.title}</Text>
            </Header>

            <PDPADialog
                open={openPDPA}
                onClose={handleClosePDPA}
                accepted={accepted}
                onCheckChange={onCheckChange}
                onAccept={onAccept}
            />
            <ConfirmDialog
                id="confirm-checkin"
                description="modal-for-confirm-checkin"
                open={open}
                contentText="We will notify your provider and create a queue card for you. After you confirm check-in."
                onCancel={handleClose}
                cancelText="Cancel"
                onConfirm={handleConfirm}
                confirmText="Confirm"
            />
            {(sessionLoading || loading) && <Progress />}

            {(appointmentStatus === STATUS.CHECK_IN ||
                appointmentStatus === STATUS.QUEUE ||
                appointmentStatus === STATUS.ON_CALLING) && (
                <AppointmentCard
                    data={appointmentData}
                    $desktopLayout={mdUp ? 1 : 0}
                    $avatarSize={mobileS ? '60px' : mdUp ? '100px' : '64px'}
                    onClick={
                        appointmentStatus === STATUS.CHECK_IN ||
                        appointmentStatus === STATUS.ON_CALLING
                            ? handleClick
                            : false
                    }
                />
            )}

            {appointmentStatus === STATUS.QUEUE && (
                <QueCard
                    margin={mdUp ? '20px 0 0 0' : '10px 0 0 0'}
                    $desktopLayout={mdUp}
                    data={queueData}
                />
            )}

            {(appointmentStatus === STATUS.CHECK_IN || appointmentStatus === STATUS.ON_CALLING) && (
                <Fragment>
                    <PatientDetailCard data={appointmentData} />
                    <SessionSummaryCard
                        margin={mdUp ? '20px 0 0 0' : '10px 0 0 0'}
                        title="Present Session Summary"
                        data={presentSession}
                        consultTime
                    />
                </Fragment>
            )}

            {(appointmentStatus === STATUS.NOTE || appointmentStatus === STATUS.SUMMARY) && (
                <ProviderInfoCard data={appointmentData?.meet.staff} />
            )}

            {appointmentStatus === STATUS.NOTE && (
                <NoteCard
                    margin={mdUp ? '20px 0 0 0' : '10px 0 0 0'}
                    onChange={(event) => handleNote(event.target.value)}
                />
            )}

            {(appointmentStatus === STATUS.NOTE || appointmentStatus === STATUS.SUMMARY) && (
                <SessionSummaryCard
                    margin={mdUp ? '20px 0 0 0' : '10px 0 0 0'}
                    title="Session Summary"
                    data={appointmentData}
                    mindgymActivity={mindgymActivity}
                    mdUp={mdUp}
                />
            )}

            {appointmentStatus === STATUS.NOTE && (
                <div
                    style={{
                        marginTop: mdUp ? '20px' : '10px',
                        display: 'flex',
                        justifyContent: mdUp ? 'flex-end' : 'center',
                        position: mdUp ? 'static' : 'fixed',
                        bottom: 0,
                        left: 0,
                        width: mdUp ? 'auto' : '100%',
                        padding: mdUp ? 0 : '20px',
                        backgroundColor: mdUp ? 'transparent' : 'white',
                    }}
                >
                    <ContainedButton
                        style={{ width: mdUp ? '240px' : '100%', height: '48px' }}
                        disabled={!note}
                        onClick={handleSubmit}
                    >
                        Submit
                    </ContainedButton>
                </div>
            )}

            {appointmentStatus === STATUS.SUCCESS && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '50px',
                    }}
                >
                    <div style={{ maxWidth: '536px' }}>
                        <Title
                            style={{
                                fontWeight: '700',
                                fontSize: '32px',
                                marginBottom: '45px',
                            }}
                        >
                            Session Successful
                        </Title>
                        <br />
                        <Text $fontSize="14px" $color="gray2">
                            You can check all the details on Medical Record page.{' '}
                        </Text>
                        <br />

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: mdUp ? 'flex-end' : 'center',
                                marginTop: '45px',
                            }}
                        >
                            <ContainedButton style={{ maxWidth: '250px' }} onClick={handleGoHome}>
                                Back to home
                            </ContainedButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

OneOnOneSessionDetail.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default OneOnOneSessionDetail;
